import { CardActionBar } from "@/components/shared/CardActionBar";
import { LinkedCopiesSection } from "@/components/shared/LinkedCopiesSection";
import { UnderlinedTokens } from "@/components/shared/UnderlinedTokens";
import { useSelection } from "@/contexts/SelectionContext";
import {
  GetLinkedItemInfoDocument,
  type LessonFieldsFragment,
  ModelEnum,
} from "@/generated/graphql";
import { THUMBNAIL } from "@/lib/constants/shared";
import { useEditLessonName } from "@/lib/hooks/lessons/useEditLessonName";
import { saveScrollTarget } from "@/lib/utils/scrollRestoration";
import type { LessonFields } from "@/types/Lesson";
import type { ViewMode } from "@/types/viewMode";
import type { Lesson } from "@ai-teach/types";
import { Card, Checkbox, IconButton, Modal } from "@ai-teach/ui-web";
import { useLazyQuery } from "@apollo/client/react";
import { type LinkProps, useNavigate } from "@tanstack/react-router";
import type React from "react";
import { useEffect, useState } from "react";
import { AiFillPicture } from "react-icons/ai";
import { MdCheck, MdClose, MdDragIndicator, MdEdit, MdLink, MdVisibilityOff } from "react-icons/md";
import { TrashCardActionBar } from "../shared/TrashCardActionBar";

interface LessonCardProps {
  lesson: LessonFields;
  viewMode?: ViewMode;
  url: "trash" | "lessonDetail" | "home";
  isSelectionMode: boolean;
  index: number | null;
}

export const LessonCard: React.FC<LessonCardProps> = ({
  lesson,
  viewMode = "grid",
  url,
  isSelectionMode,
  index,
}) => {
  const isList = viewMode === "list";

  const [showLimitModal, setShowLimitModal] = useState(false);
  const [isEditingName, setIsEditingName] = useState<boolean>(false);
  const [lessonName, setLessonName] = useState<string>(lesson.name);

  const { selectedLessonIds, toggleLesson, toggleWarningModal } = useSelection();
  const isSelected = lesson.id ? selectedLessonIds.includes(lesson.id) : false;

  const navigate = useNavigate();

  useEffect(() => {
    if (!isEditingName) {
      setLessonName(lesson.name);
    }
  }, [lesson.name, isEditingName]);

  const { editLessonName } = useEditLessonName();

  const handleSaveName = () => {
    if (lessonName.trim() && lessonName !== lesson.name) {
      editLessonName(lesson.id, lessonName);
    } else {
      setLessonName(lesson.name);
    }
    setIsEditingName(false);
  };

  const handleCancelEdit = () => {
    setLessonName(lesson.name);
    setIsEditingName(false);
  };

  const originalLessonId = lesson.originalLessonId ?? null;
  const isLinkedCopy = lesson.isLinkedCopy ?? false;
  const isHidden = lesson.isHidden ?? false;
  const hasImage = !!(lesson.imageId && lesson.imageUrl);

  const [getLinkedItemInfoQuery] = useLazyQuery(GetLinkedItemInfoDocument);

  const handleRedirectToOriginal = async () => {
    if (!originalLessonId) return;
    const linkedItem = await getLinkedItemInfoQuery({
      variables: { id: originalLessonId, model: ModelEnum.Lesson },
    });
    const itemData = linkedItem.data?.linkedItemInfo;
    if (!itemData) return;
    let url: LinkProps["to"];
    saveScrollTarget(`lesson-${originalLessonId}`, itemData.parentId || "home");
    if (itemData.parentId) {
      url = itemData.isTrash ? "/admin/project/$projectId/trash" : "/admin/project/$projectId";
      navigate({ to: url, params: { projectId: itemData.parentId } });
    } else {
      url = itemData.isTrash ? "/admin/trash" : "/admin";
      navigate({ to: url });
    }
  };

  const handleClickLinked = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!originalLessonId) return;
    toggleWarningModal("You will be redirected to the original item page. Continue?", {
      onCancel: () => toggleWarningModal(false),
      onConfirm: handleRedirectToOriginal,
    });
  };

  // W2: flatten a `\n`-joined limit/focus array into trimmed entries for the card preview.
  const flattenField = (arr: string[] | undefined) =>
    (arr ?? []).flatMap((item) =>
      item
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean)
    );
  // Teachers author only Focus; the limit is derived (accumulated) and shown in the modal below.
  const lessonFields = [
    { label: "Word Focus", items: flattenField(lesson.limitation.wordFocus) },
    { label: "Grammar Focus", items: flattenField(lesson.limitation.grammarFocus) },
  ];
  const accWordLimit = lesson.accumulatedLimit?.wordLimit ?? [];
  const accGrammarLimit = lesson.accumulatedLimit?.grammarLimit ?? [];
  // Per-lesson breakdown of the accumulated known-set (curriculum order). The flat arrays above are
  // only used for the modal-trigger count badge.
  const accByLesson = lesson.accumulatedLimit?.byLesson ?? [];
  const accWordGroups = accByLesson.filter((g) => g.words.length > 0);
  const accGrammarGroups = accByLesson.filter((g) => g.grammar.length > 0);
  // Show the source project on each lesson sub-header only when the set spans more than one project.
  const showProjectPrefix = new Set(accByLesson.map((g) => g.projectName).filter(Boolean)).size > 1;
  const groupHeader = (g: { lessonName: string; projectName: string | null }) =>
    showProjectPrefix && g.projectName ? `${g.projectName} — ${g.lessonName}` : g.lessonName;
  const PREVIEW_COUNT = 3;

  // Thumbnail inner content shared between both views
  const thumbnailInner = (
    <>
      {isSelectionMode && (
        <div className="absolute top-2 left-2 z-10">
          <Checkbox
            checked={isSelected}
            onChange={() => {
              if (lesson.id)
                toggleLesson(lesson.id, lesson.isLinkedCopy, lesson.sortOrder ?? undefined);
            }}
            size="lg"
          />
        </div>
      )}
      {hasImage ? (
        <img
          src={lesson.imageUrl || "placeholder"}
          alt={lesson.name}
          className="w-full h-full object-cover"
        />
      ) : isList ? (
        <AiFillPicture className="w-12 h-12 text-gray-500 dark:text-gray-400" aria-hidden="true" />
      ) : (
        <div className="text-gray-500 dark:text-gray-400 flex flex-col items-center justify-center gap-2">
          <AiFillPicture className="w-16 h-16" />
          <p className="text-sm font-medium">{THUMBNAIL.lessonAspectRatioString}</p>
        </div>
      )}
    </>
  );

  const cardContent = (
    <Card
      hoverable={!isSelectionMode}
      className={`cursor-pointer relative ${isList ? "shadow-lg" : "shadow-xl"} ${
        isLinkedCopy ? "ring-2 ring-secondary-500" : ""
      } ${isSelected ? "ring-4 ring-primary-500 bg-primary-500/10" : ""} ${
        isHidden ? "opacity-75 border-dashed border-2 border-gray-400 dark:border-gray-600" : ""
      }`}
      onClick={() => {
        if (lesson.id && isSelectionMode)
          toggleLesson(lesson.id, lesson.isLinkedCopy, lesson.sortOrder ?? undefined);
      }}
    >
      {/* Action bar */}
      {isSelectionMode ? null : url === "trash" ? (
        <TrashCardActionBar id={lesson.id} type="lesson" lesson={lesson} />
      ) : (
        <CardActionBar id={lesson.id || ""} type="lesson" lesson={lesson} />
      )}

      {/* Linked badge */}
      {isLinkedCopy && originalLessonId && (
        <button
          type="button"
          onClick={handleClickLinked}
          className="absolute top-3 left-3 z-20 group/link"
        >
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-500/80 backdrop-blur-sm text-white rounded-full shadow-md hover:shadow-lg transition-all duration-200">
            <MdLink className="w-3.5 h-3.5" />
            <span className="text-xs font-semibold">Linked</span>
          </div>
        </button>
      )}

      {/* Hidden badge */}
      {isHidden && (
        <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5 px-2.5 py-1 bg-muted/80 backdrop-blur-sm text-white rounded-full shadow-md">
          <MdVisibilityOff className="w-3.5 h-3.5" />
          <span className="text-xs font-medium">Hidden</span>
        </div>
      )}

      {/* Grid-only: sort badge top-right */}
      {!isList && index !== null && !isHidden && (
        <div className="absolute top-3 right-3 z-20 group/sort">
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-primary-900 backdrop-blur-sm text-white rounded-full shadow-md hover:shadow-lg hover:bg-gray-500 transition-all duration-200 cursor-grab select-none">
            <MdDragIndicator className="w-3.5 h-3.5 opacity-60 group-hover/sort:opacity-100 transition-opacity duration-200" />
            <span className="text-xs font-semibold">{index + 1}</span>
          </div>
          <div className="absolute top-full right-0 mt-2 pointer-events-none opacity-0 group-hover/sort:opacity-100 transition-opacity duration-200 delay-300">
            <div className="relative bg-gray-900 text-white text-xs rounded-md px-2.5 py-1.5 shadow-lg whitespace-nowrap">
              <span className="absolute -top-1 right-3 w-2 h-2 bg-gray-900 rotate-45 block" />
              Drag card to reorder
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <div className={isList ? "flex items-center gap-5 p-4" : ""}>
        {/* List-only: inline drag handle */}
        {isList && index !== null && (
          <div className="group/sort flex-shrink-0 relative">
            <div className="flex flex-col items-center gap-0.5 px-1.5 py-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-grab transition-colors duration-200 select-none rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
              <MdDragIndicator className="w-5 h-5" />
              <span className="text-xs font-semibold leading-none">{index + 1}</span>
            </div>
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 pointer-events-none opacity-0 group-hover/sort:opacity-100 transition-opacity duration-200 delay-300 z-30">
              <div className="relative bg-gray-900 text-white text-xs rounded-md px-2.5 py-1.5 shadow-lg whitespace-nowrap">
                <span className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
                Drag card to reorder
              </div>
            </div>
          </div>
        )}

        {/* Thumbnail */}
        {isList ? (
          <div
            className={`flex-shrink-0 w-32 ${THUMBNAIL.lessonAspectRatioClass} rounded-lg flex items-center justify-center overflow-hidden relative border border-gray-200 dark:border-gray-800 shadow-sm ${
              hasImage ? "bg-surface" : "bg-gray-200 dark:bg-gray-800"
            } ${isHidden ? "grayscale opacity-60" : ""}`}
          >
            {thumbnailInner}
          </div>
        ) : (
          <div className="relative rounded-2xl overflow-hidden">
            <div
              className={`${THUMBNAIL.lessonAspectRatioClass} flex items-center justify-center rounded-t-xl overflow-hidden border-b-2 border-gray-200 dark:border-gray-800 ${
                hasImage ? "bg-surface" : "bg-gray-200 dark:bg-gray-800"
              } ${isHidden ? "grayscale opacity-60" : ""}`}
            >
              {thumbnailInner}
            </div>
          </div>
        )}

        {/* List-only: vertical divider */}
        {isList && (
          <div className="h-24 w-px bg-gradient-to-b from-transparent via-gray-300 dark:via-gray-700 to-transparent" />
        )}

        {/* Content */}
        <div
          className={isList ? "flex-1 min-w-0" : "p-4 pt-5 group/name overflow-hidden"}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className={`group/title flex items-center gap-2 ${!isList ? "mb-1" : ""}`}>
            <div className="flex-1">
              {isList ? (
                <input
                  type="text"
                  value={lessonName}
                  readOnly={!isEditingName}
                  className={`w-full truncate bg-transparent border-none outline-none card-title ${
                    isEditingName ? "ring-2 ring-blue-500 rounded px-2 py-1" : "px-0"
                  }`}
                  onChange={(e) => {
                    setLessonName(e.target.value);
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isEditingName) setIsEditingName(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveName();
                    else if (e.key === "Escape") handleCancelEdit();
                    e.stopPropagation();
                  }}
                  onBlur={handleSaveName}
                />
              ) : (
                <textarea
                  value={lessonName}
                  readOnly={!isEditingName}
                  rows={2}
                  className={`w-full resize-none bg-transparent border-none outline-none card-title ${
                    isEditingName
                      ? "ring-2 ring-blue-500 rounded px-2 py-1 min-h-[3.5rem]"
                      : "px-0 line-clamp-2 min-h-[3.5rem]"
                  }`}
                  onChange={(e) => {
                    setLessonName(e.target.value);
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isEditingName) setIsEditingName(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSaveName();
                    } else if (e.key === "Escape") {
                      handleCancelEdit();
                    }
                    e.stopPropagation();
                  }}
                  onBlur={handleSaveName}
                />
              )}
            </div>

            {!isEditingName ? (
              <IconButton
                variant="ghost"
                size="sm"
                aria-label="Edit lesson name"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsEditingName(true);
                }}
                className={`opacity-0 group-hover/title:opacity-100 transition-opacity duration-200 ${!isList ? "self-start" : ""}`}
              >
                <MdEdit className="w-4 h-4" aria-hidden="true" />
              </IconButton>
            ) : (
              <div className={`flex gap-1 flex-shrink-0 ${!isList ? "self-start" : ""}`}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label="Save"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSaveName();
                  }}
                  className="hover:bg-success-700/20"
                >
                  <MdCheck className="w-4 h-4 text-success-500" />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label="Cancel"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCancelEdit();
                  }}
                  className="hover:bg-error-700/20"
                >
                  <MdClose className="w-4 h-4 text-error-500" />
                </IconButton>
              </div>
            )}
          </div>

          {/* W2: focus + limit preview (4 fields, truncated) with a modal for the full
              accumulated known-set. */}
          <div className="mt-3 rounded-lg border border-primary-100 dark:border-primary-900/40 divide-y divide-primary-100 dark:divide-primary-900/40">
            {lessonFields.map((f) => (
              <div key={f.label} className="px-3 py-1.5">
                <span className="text-[10px] font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                  {f.label}
                </span>
                {f.items.length > 0 ? (
                  <p className="label leading-relaxed line-clamp-1">
                    {f.items.slice(0, PREVIEW_COUNT).join(", ")}
                    {f.items.length > PREVIEW_COUNT
                      ? ` +${f.items.length - PREVIEW_COUNT} more`
                      : ""}
                  </p>
                ) : (
                  <p className="text-gray-400 dark:text-gray-500 text-xs italic">None</p>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowLimitModal(true);
              }}
              className="w-full px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
            >
              View full focus &amp; accumulated ({accWordLimit.length + accGrammarLimit.length})
            </button>
          </div>

          <Modal
            isOpen={showLimitModal}
            onClose={() => setShowLimitModal(false)}
            title={`${lesson.name} \u2014 focus & accumulated known-set`}
            size="md"
          >
            <div className="flex flex-col gap-4">
              {/* This lesson's authored Focus, in full (the card preview truncates to a few). */}
              <section>
                <h4 className="text-sm font-semibold text-primary-700 mb-2">
                  This lesson&apos;s focus (authored)
                </h4>
                <div className="flex flex-col gap-3">
                  {lessonFields.map((f) => (
                    <div key={f.label}>
                      <span className="text-[10px] font-semibold text-primary-600 uppercase tracking-wide">
                        {f.label}
                      </span>
                      {f.items.length > 0 ? (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {f.items.map((it) => (
                            <span
                              key={it}
                              className="text-xs px-2 py-0.5 bg-primary-50 text-primary-800 border border-primary-200 rounded"
                            >
                              {it}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs italic text-gray-400 mt-1">None</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
              <section>
                <h4 className="text-sm font-semibold text-primary-700 mb-1">
                  Word limit (accumulated)
                </h4>
                {accWordGroups.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    {accWordGroups.map((group) => (
                      <div key={group.lessonId}>
                        <span className="text-[10px] font-semibold text-primary-600 uppercase tracking-wide">
                          {groupHeader(group)}
                        </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {group.words.map((w) => (
                            <span
                              key={w}
                              className="text-xs px-2 py-0.5 bg-green-50 text-green-800 border border-green-200 rounded"
                            >
                              {w}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs italic text-gray-400">None</p>
                )}
              </section>
              <section>
                <h4 className="text-sm font-semibold text-primary-700 mb-1">
                  Grammar limit (accumulated)
                </h4>
                {accGrammarGroups.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    {accGrammarGroups.map((group) => (
                      <div key={group.lessonId}>
                        <span className="text-[10px] font-semibold text-primary-600 uppercase tracking-wide">
                          {groupHeader(group)}
                        </span>
                        <div className="flex flex-col gap-1 mt-1">
                          {group.grammar.map((g) => (
                            <p
                              key={g}
                              className="text-sm text-gray-700 bg-gray-50 p-1.5 rounded border border-gray-200"
                            >
                              <UnderlinedTokens text={g} />
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs italic text-gray-400">None</p>
                )}
              </section>
            </div>
          </Modal>

          <LinkedCopiesSection
            linkedCopies={lesson.linkedCopies}
            type="Lesson"
            originalItemId={lesson.id}
          />
        </div>
      </div>
    </Card>
  );

  const handleCardClick = () => {
    if (lesson.id) {
      saveScrollTarget(`lesson-${lesson.id}`, lesson.parentId || "home");
    }
    navigate({ to: "/admin/lesson/$lessonId", params: { lessonId: lesson.id } });
  };

  if (isSelectionMode || url === "trash") {
    return <div id={`lesson-${lesson.id}`}>{cardContent}</div>;
  }

  return (
    <div id={`lesson-${lesson.id}`}>
      <Card onClick={handleCardClick} className="cursor-pointer">
        {cardContent}
      </Card>
    </div>
  );
};
